import React from 'react';
import { Button, type Variant } from './Button';
import { Dropdown } from './Dropdown';

// PageHeader - the ONE page-header template for list/management pages (UI/UX hand-off W-M9, W-iP8).
//
// What it fixes. Every page hand-rolled its own title row, so the header height and its left edge
// varied (10/13/17px) and the title and buttons jumped when moving between pages; the button order
// varied (the primary sat mid-group on PCR); view toggles sat left, centre or bottom-right; and
// Quantified Work collapsed Refresh/Search/Add Task/Import to bare icons below ~1912px.
//
// The template:
//   - title left, actions right, on one row with a fixed minimum height so every page's header is
//     the same size whether it has zero buttons or four;
//   - actions in a fixed order by intent - neutral (Refresh, Export, Print), then secondary
//     (Search, Import), then danger - with the primary/create action ALWAYS last;
//   - every action keeps its text label at every width. There is no icon-only mode. An action
//     marked `overflow` moves into a labelled "More" menu on a phone, where four labelled buttons
//     do not fit; from `md` (768px, so every iPad orientation) it is an ordinary labelled button;
//   - a `viewToggle` slot UNDER the title for view switchers (Chart/Table, List/Map,
//     Today/Weeks/Months), so a toggle is never left, centre and bottom-right on different pages;
//   - no padding of its own: the page container (`ListPage`) owns the gutter, so the left edge is
//     the same on every page.
export type PageActionIntent = 'primary' | 'secondary' | 'neutral' | 'danger';

/** Intent -> Button variant. Keeps colour meaning consistent across pages. */
const ACTION_INTENT: Record<PageActionIntent, Variant> = {
  primary: 'primary', // the page's create/main action
  secondary: 'outline-primary', // meaningful but not the main action (Search, Import)
  neutral: 'outline-secondary', // chrome: Refresh, Export, Print
  danger: 'outline-danger',
};

/** Fixed left-to-right order of the intent groups; the primary action is always last. */
const INTENT_ORDER: Record<PageActionIntent, number> = {
  neutral: 0,
  secondary: 1,
  danger: 2,
  primary: 3,
};

export interface PageAction {
  key: string;
  label: string;
  /** Bootstrap icon class, e.g. 'bi-plus-lg'. */
  icon: string;
  onClick: () => void;
  intent?: PageActionIntent;
  /** Renders as a filled/active control (e.g. Search while the search row is open). */
  active?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  /** Overrides the derived variant when a page genuinely needs something else. */
  variant?: Variant;
  /**
   * Move this action into the labelled "More" menu on a phone (< md) where the bar is genuinely
   * short of space. Never set it on the primary action. Ignored from md up.
   */
  overflow?: boolean;
  /** @deprecated Labels are always shown now (W-iP8); kept so existing callers compile. */
  alwaysShowLabel?: boolean;
  title?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: PageAction[];
  /** View switcher (a SegmentedControl) - rendered under the title, left-aligned. */
  viewToggle?: React.ReactNode;
  /** @deprecated Pass the view switcher as `viewToggle`. Rendered in the same slot. */
  children?: React.ReactNode;
  className?: string;
}

/** Visible actions in the template's fixed order (stable within an intent group). */
export function orderPageActions(actions: PageAction[]): PageAction[] {
  return actions
    .filter((a) => !a.hidden)
    .map((a, index) => ({ a, index }))
    .sort((x, y) => {
      const rx = INTENT_ORDER[x.a.intent ?? 'neutral'];
      const ry = INTENT_ORDER[y.a.intent ?? 'neutral'];
      return rx === ry ? x.index - y.index : rx - ry;
    })
    .map(({ a }) => a);
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions = [],
  viewToggle,
  children,
  className = '',
}) => {
  const ordered = orderPageActions(actions);
  const overflowActions = ordered.filter((a) => a.overflow && (a.intent ?? 'neutral') !== 'primary');
  const toggle = viewToggle ?? children;

  return (
    <header data-slot="page-header" className={`tw-mb-3 tw-shrink-0 ${className}`}>
      <div className="tw-flex tw-min-h-[2.5rem] tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-2">
        <div className="tw-min-w-0 tw-flex-1">
          <h1 className="tw-mb-0 tw-truncate tw-text-2xl tw-font-semibold tw-leading-tight tw-text-content">{title}</h1>
          {subtitle && <p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">{subtitle}</p>}
        </div>

        {ordered.length > 0 && (
          // ml-auto keeps the block on the right even when it wraps under the title on a phone.
          <div
            data-slot="page-header-actions"
            className="tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2"
          >
            {ordered.map((a) => {
              const intent = a.intent ?? 'neutral';
              const variant = a.variant ?? (a.active ? 'primary' : ACTION_INTENT[intent]);
              const inOverflow = overflowActions.includes(a);
              return (
                <Button
                  key={a.key}
                  type="button"
                  size="sm"
                  variant={variant}
                  onClick={a.onClick}
                  disabled={a.disabled}
                  title={a.title ?? a.label}
                  data-action={a.key}
                  data-intent={intent}
                  className={`tw-whitespace-nowrap ${inOverflow ? 'max-md:tw-hidden' : ''}`}
                >
                  <i className={`bi ${a.icon} tw-mr-1.5`} aria-hidden="true" />
                  <span>{a.label}</span>
                </Button>
              );
            })}
            {overflowActions.length > 0 && (
              // Phones only: the overflowed actions, still with their labels.
              <Dropdown align="end" className="md:tw-hidden" data-slot="page-header-more">
                <Dropdown.Toggle className="tw-text-sm" aria-label="More actions">
                  <i className="bi bi-three-dots tw-mr-1" aria-hidden="true" />
                  More
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {overflowActions.map((a) => (
                    <Dropdown.Item key={a.key} onClick={a.onClick} disabled={a.disabled}>
                      <i className={`bi ${a.icon} tw-mr-2`} aria-hidden="true" />
                      {a.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        )}
      </div>

      {toggle && (
        <div data-slot="page-header-view-toggle" className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {toggle}
        </div>
      )}
    </header>
  );
};

export default PageHeader;
